import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Select from "@mui/material/Select";
import { styled, useTheme } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import AssignmentIcon from "@mui/icons-material/Assignment";
import ChatIcon from "@mui/icons-material/Chat";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import PeopleIcon from "@mui/icons-material/People";
import SendIcon from "@mui/icons-material/Send";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import React, { useCallback, useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import api from "../../services/api";
import { AuthContext } from "../../context/Auth/AuthContext";
import useMessages from "../../hooks/useMessages";
import useTickets from "../../hooks/useTickets";
import useUsers from "../../hooks/useUsers";
import ChartMessages from "./ChartMessages";
import ChartPerUser from "./ChartPerUser";
import NewContactsChart from "./NewContactsChart";
import ContactsWithTicketsChart from "./ContactsWithTicketsChart";
import TagCloud from "./TagCloud";

const StyledContainer = styled(Container)(({ theme }) => ({
	paddingTop: theme.spacing(2),
	paddingBottom: theme.spacing(2),
	[theme.breakpoints.down('sm')]: {
		paddingTop: theme.spacing(1),
		paddingBottom: theme.spacing(1),
		paddingLeft: theme.spacing(1),
		paddingRight: theme.spacing(1),
	},
}));

const DashboardSection = styled('div')(({ theme }) => ({
	marginBottom: theme.spacing(4),
	[theme.breakpoints.down('sm')]: {
		marginBottom: theme.spacing(2),
	},
}));

const FixedHeightPaperCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2.5, 2),
  background: theme.palette.mode === 'dark'
    ? theme.palette.background.paper
    : `linear-gradient(135deg, ${theme.palette.primary.light}15 0%, ${theme.palette.background.paper} 100%)`,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: 18,
  boxShadow: theme.palette.mode === 'dark'
    ? "0 8px 32px 0 rgba(0, 0, 0, 0.5), 0 2px 8px 0 rgba(0, 0, 0, 0.3)"
    : "0 8px 32px 0 rgba(80, 80, 160, 0.12), 0 2px 8px 0 rgba(80, 80, 160, 0.08)",
  minHeight: 130,
  border: theme.palette.mode === 'dark'
    ? `2px solid ${theme.palette.divider}`
    : `1px solid ${theme.palette.grey[200]}`,
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: theme.palette.mode === 'dark'
      ? "0 12px 40px 0 rgba(0, 0, 0, 0.6), 0 4px 12px 0 rgba(0, 0, 0, 0.4)"
      : "0 12px 40px 0 rgba(80, 80, 160, 0.16), 0 4px 12px 0 rgba(80, 80, 160, 0.12)",
  },
  [theme.breakpoints.down('sm')]: {
    minHeight: 100,
    padding: theme.spacing(1.5, 1),
  },
}));

const CardIconCircle = styled('div')(({ theme, bgcolor }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 48,
  height: 48,
  borderRadius: '50%',
  background: bgcolor || theme.palette.primary.main,
  boxShadow: '0 2px 8px 0 rgba(80, 80, 160, 0.10)',
  marginBottom: theme.spacing(1),
}));

const CardText = styled(Typography)(({ theme }) => ({
	fontSize: "1.2rem",
	marginLeft: theme.spacing(1),
	[theme.breakpoints.down('sm')]: {
		fontSize: "1rem",
		marginLeft: theme.spacing(0.5),
	},
}));

const CardTitle = styled(Typography)(({ theme }) => ({
	fontSize: "0.9rem",
	color: theme.palette.primary.dark,
	marginTop: theme.spacing(1),
	[theme.breakpoints.down('sm')]: {
		fontSize: "0.8rem",
		marginTop: theme.spacing(0.5),
		textAlign: "center",
	},
}));

const CardContent = styled('div')({
	display: 'flex',
	alignItems: 'center',
});


const ChartPaperCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  background: theme.palette.mode === 'dark'
    ? theme.palette.background.paper
    : `linear-gradient(135deg, ${theme.palette.primary.light}15 0%, ${theme.palette.background.paper} 100%)`,
  borderRadius: 18,
  boxShadow: theme.palette.mode === 'dark'
    ? "0 8px 32px 0 rgba(0, 0, 0, 0.5), 0 2px 8px 0 rgba(0, 0, 0, 0.3)"
    : "0 8px 32px 0 rgba(80, 80, 160, 0.12), 0 2px 8px 0 rgba(80, 80, 160, 0.08)",
  border: theme.palette.mode === 'dark'
    ? `2px solid ${theme.palette.divider}`
    : `1px solid ${theme.palette.grey[200]}`,
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: theme.palette.mode === 'dark'
      ? "0 12px 40px 0 rgba(0, 0, 0, 0.6), 0 4px 12px 0 rgba(0, 0, 0, 0.4)"
      : "0 12px 40px 0 rgba(80, 80, 160, 0.16), 0 4px 12px 0 rgba(80, 80, 160, 0.12)",
  },
}));

const SOURCE_COLORS = {
	"Instagram":             "#E1306C",
	"Facebook Messenger":    "#0099FF",
	"WhatsApp via Anúncio":  "#FF6B35",
	"WhatsApp Direto":       "#25D366",
};
const SOURCE_DEFAULT_COLOR = "#9c27b0";

const BarRow = ({ label, value, maxVal, color, isDark }) => (
	<Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
		<Typography sx={{ minWidth: 130, fontSize: "0.82rem", fontWeight: 500, color: "text.primary",
			overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flexShrink: 0 }}>
			{label}
		</Typography>
		<Box sx={{ flex: 1, height: 20, borderRadius: 2, bgcolor: isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.05)", overflow: "hidden", minWidth: 40 }}>
			<Box sx={{
				height: "100%", borderRadius: 2,
				width: `${Math.round((value / maxVal) * 100)}%`,
				background: color,
				transition: "width 0.5s ease",
			}} />
		</Box>
		<Typography sx={{ minWidth: 28, fontSize: "0.82rem", fontWeight: 700, color, textAlign: "right", flexShrink: 0 }}>
			{value}
		</Typography>
	</Box>
);

const LeadMetricsCard = ({ whatsappId }) => {
	const theme = useTheme();
	const isDark = theme.palette.mode === "dark";
	const today = new Date().toISOString().slice(0, 10);
	const weekAgo = new Date(Date.now() - 6 * 86400000).toISOString().slice(0, 10);
	const [dateFrom, setDateFrom] = React.useState(weekAgo);
	const [dateTo, setDateTo] = React.useState(today);
	const [channelData, setChannelData] = React.useState([]);
	const [sourceData, setSourceData] = React.useState([]);
	const [loading, setLoading] = React.useState(false);

	const load = React.useCallback(async () => {
		setLoading(true);
		try {
			const chanParam = whatsappId ? `&whatsappId=${whatsappId}` : '';
			const [chRes, srcRes] = await Promise.all([
				api.get(`/tickets/by-channel?dateFrom=${dateFrom}&dateTo=${dateTo}${chanParam}`),
				api.get(`/tickets/by-source?dateFrom=${dateFrom}&dateTo=${dateTo}${chanParam}`),
			]);
			setChannelData(chRes.data || []);
			const src = srcRes.data || {};
			const srcArr = [
				{ source: "Instagram", count: Number(src.instagram || 0) },
				{ source: "Facebook Messenger", count: Number(src.messenger || 0) },
				{ source: "WhatsApp via Anúncio", count: Number(src.whatsappAds || 0) },
				{ source: "WhatsApp Direto", count: Number(src.whatsappDirect || 0) },
			].filter(d => d.count > 0);
			setSourceData(srcArr);
		} catch(e) { console.error(e); }
		setLoading(false);
	}, [dateFrom, dateTo, whatsappId]);

	useEffect(() => { load(); }, [load]);

	const inputStyle = {
		background: isDark ? "rgba(255,255,255,0.05)" : "#f5f7fa",
		color: isDark ? "#fff" : "#222",
		border: `1px solid ${isDark ? "rgba(255,255,255,0.12)" : "#ddd"}`,
		borderRadius: 8, padding: "4px 10px", fontSize: 13,
		outline: "none",
	};

	const subCardStyle = {
		p: 2, borderRadius: "12px",
		background: isDark ? "rgba(255,255,255,0.03)" : "rgba(255,255,255,0.7)",
		border: `1px solid ${isDark ? "rgba(255,255,255,0.06)" : theme.palette.grey[100]}`,
	};

	const maxChannel = Math.max(...channelData.map(d => Number(d.uniqueContacts)), 1);
	const maxSource  = Math.max(...sourceData.map(d => Number(d.count)), 1);
	const totalLeads = sourceData.reduce((s, d) => s + Number(d.count), 0);

	return (
		<ChartPaperCard>
			{/* Header */}
			<Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3, flexWrap: "wrap", gap: 1 }}>
				<Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
					<Box sx={{
						display: 'flex', alignItems: 'center', justifyContent: 'center',
						width: 42, height: 42, borderRadius: '50%',
						background: `${theme.palette.primary.main}20`,
						boxShadow: '0 2px 8px 0 rgba(80, 80, 160, 0.15)',
						flexShrink: 0,
					}}>
						<TrendingUpIcon sx={{ fontSize: 22, color: theme.palette.primary.main }} />
					</Box>
					<Box>
						<Typography variant="subtitle1" fontWeight={700} color="primary.main" sx={{ lineHeight: 1.2 }}>
							Métricas de Leads Novos
						</Typography>
						{totalLeads > 0 && (
							<Typography variant="caption" color="text.secondary">
								{totalLeads} contatos novos no período
							</Typography>
						)}
					</Box>
				</Box>
				<Box sx={{ display: "flex", gap: 1, alignItems: "flex-end", flexWrap: "wrap" }}>
					<Box sx={{ display: "flex", flexDirection: "column" }}>
						<Typography variant="caption" color="text.secondary" sx={{ mb: 0.3, fontWeight: 500 }}>De</Typography>
						<input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} style={inputStyle} />
					</Box>
					<Box sx={{ display: "flex", flexDirection: "column" }}>
						<Typography variant="caption" color="text.secondary" sx={{ mb: 0.3, fontWeight: 500 }}>Até</Typography>
						<input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} style={inputStyle} />
					</Box>
				</Box>
			</Box>

			{loading ? (
				<Box sx={{ display: "flex", justifyContent: "center", py: 5 }}>
					<CircularProgress size={36} />
				</Box>
			) : (
				<Grid container spacing={3}>
					<Grid item xs={12} md={6}>
						<Box sx={subCardStyle}>
							<Typography variant="caption" fontWeight={700} sx={{ color: "text.secondary", textTransform: "uppercase", letterSpacing: 1, fontSize: "0.7rem" }}>
								Contatos Únicos por Canal
							</Typography>
							<Box sx={{ mt: 1.5, display: "flex", flexDirection: "column", gap: 1.2 }}>
								{channelData.length === 0 ? (
									<Typography color="text.secondary" sx={{ fontSize: "0.83rem", py: 1 }}>Sem dados no período</Typography>
								) : (
									channelData.map((row, i) => (
										<BarRow
											key={i}
											label={row.channel || row.channelName || "Canal"}
											value={Number(row.uniqueContacts)}
											maxVal={maxChannel}
											color={`hsl(${(i * 53 + 200) % 360}, 65%, 50%)`}
											isDark={isDark}
										/>
									))
								)}
							</Box>
						</Box>
					</Grid>

					<Grid item xs={12} md={6}>
						<Box sx={subCardStyle}>
							<Typography variant="caption" fontWeight={700} sx={{ color: "text.secondary", textTransform: "uppercase", letterSpacing: 1, fontSize: "0.7rem" }}>
								Origem dos Leads
							</Typography>
							<Box sx={{ mt: 1.5, display: "flex", flexDirection: "column", gap: 1.2 }}>
								{sourceData.length === 0 ? (
									<Typography color="text.secondary" sx={{ fontSize: "0.83rem", py: 1 }}>Sem dados no período</Typography>
								) : (
									sourceData.map((row, i) => (
										<BarRow
											key={i}
											label={row.source}
											value={Number(row.count)}
											maxVal={maxSource}
											color={SOURCE_COLORS[row.source] || SOURCE_DEFAULT_COLOR}
											isDark={isDark}
										/>
									))
								)}
							</Box>
						</Box>
					</Grid>
				</Grid>
			)}
		</ChartPaperCard>
	);
};

const Dashboard = () => {
	const theme = useTheme();
	const isDash = theme.palette.mode === "dark";
	const { t } = useTranslation();
	const { user } = useContext(AuthContext);
	const userQueueIds = user?.queues?.map(q => q.id) || [];
	const isAdmin = user?.profile === 'admin';
	const isMasterAdmin = user?.profile?.toUpperCase() === 'MASTERADMIN';

	// ── Seletor de canal global ──────────────────────────────────────────
	const [selectedChannel, setSelectedChannel] = useState("");
	const [channels, setChannels] = useState([]);

	useEffect(() => {
		api.get("/tickets/channels")
			.then(r => setChannels(r.data || []))
			.catch(() => {});
	}, []);

	const channelIds = selectedChannel ? JSON.stringify([Number(selectedChannel)]) : undefined;

	const { count: usersCount, online: onlineUsers } = useUsers();
	const { sent: sentMessages, received: receivedMessages } = useMessages({ whatsappId: selectedChannel || undefined });

	const [previousCounts, setPreviousCounts] = useState({
		inAttendance: 0,
		waiting: 0,
		closed: 0,
		users: 0,
	});

	const ticketsInAttendance = useTickets({
		status: "open",
		withUnreadMessages: "false",
		queueIds: JSON.stringify(userQueueIds),
		channelIds,
		all: "false"
	});

	const ticketsWaiting = useTickets({
		status: "pending",
		withUnreadMessages: "false",
		queueIds: JSON.stringify(userQueueIds),
		channelIds,
		all: "false"
	});

	const ticketsClosed = useTickets({
		status: "closed",
		withUnreadMessages: "false",
		queueIds: JSON.stringify(userQueueIds),
		channelIds,
		all: "false"
	});

	useEffect(() => {
		if (
			ticketsInAttendance.count !== previousCounts.inAttendance ||
			ticketsWaiting.count !== previousCounts.waiting ||
			ticketsClosed.count !== previousCounts.closed ||
			usersCount !== previousCounts.users
		) {
			setPreviousCounts({
				inAttendance: ticketsInAttendance.count,
				waiting: ticketsWaiting.count,
				closed: ticketsClosed.count,
				users: usersCount,
			});
		}
	}, [
		ticketsInAttendance.count,
		ticketsWaiting.count,
		ticketsClosed.count,
		usersCount,
		previousCounts,
	]);

	const renderChangeIcon = (current, previous) => {
		if (current > previous) {
			return (
				<ArrowUpwardIcon
					sx={{ marginLeft: 0.5, fontSize: "1rem", color: "green" }}
				/>
			);
		} else if (current < previous) {
			return (
				<ArrowDownwardIcon
					sx={{ marginLeft: 0.5, fontSize: "1rem", color: "red" }}
				/>
			);
		}
		return null;
	};

	return (
		<StyledContainer maxWidth="lg">
			{/* Seletor de canal global */}
			{channels.length > 0 && (
				<Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2, flexWrap: "wrap" }}>
					<Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
						Filtrar por canal:
					</Typography>
					<Select
						value={selectedChannel}
						onChange={e => setSelectedChannel(e.target.value)}
						displayEmpty
						size="small"
						sx={{
							minWidth: 200,
							borderRadius: 2,
							background: isDash ? "rgba(255,255,255,0.05)" : "#f5f7fa",
							"& .MuiOutlinedInput-notchedOutline": { borderColor: isDash ? "rgba(255,255,255,0.12)" : "#ddd" },
						}}
					>
						<MenuItem value=""><em>Todos os canais</em></MenuItem>
						{channels.map(ch => (
							<MenuItem key={ch.id} value={String(ch.id)}>{ch.name}</MenuItem>
						))}
					</Select>
				</Box>
			)}
			<DashboardSection>
				<Grid container spacing={3}>
					<Grid item xs={12} sm={4}>
						<FixedHeightPaperCard>
							<CardContent sx={{ flexDirection: 'column', alignItems: 'center', justifyContent: 'center', mb: 1 }}>
								<CardIconCircle bgcolor="#6c63ff20">
									<AssignmentIcon sx={{ fontSize: 28, color: theme => theme.palette.primary.main }} />
								</CardIconCircle>
								<CardText component="h1" variant="h4" sx={{ fontWeight: 700, color: 'text.primary', mb: 0.5 }}>
									{ticketsInAttendance.count}
									{renderChangeIcon(
										ticketsInAttendance.count,
										previousCounts.inAttendance
									)}
								</CardText>
							</CardContent>
							<CardTitle component="h3" sx={{ color: 'text.primary' }}>
								{t("dashboard.messages.inAttendance.title")}
							</CardTitle>
						</FixedHeightPaperCard>
					</Grid>

					<Grid item xs={12} sm={4}>
						<FixedHeightPaperCard>
							<CardContent sx={{ flexDirection: 'column', alignItems: 'center', justifyContent: 'center', mb: 1 }}>
								<CardIconCircle bgcolor="#ffd60030">
									<HourglassEmptyIcon sx={{ fontSize: 28, color: theme => theme.palette.warning.main }} />
								</CardIconCircle>
								<CardText component="h1" variant="h4" sx={{ fontWeight: 700, color: 'text.primary', mb: 0.5 }}>
									{ticketsWaiting.count}
									{renderChangeIcon(
										ticketsWaiting.count,
										previousCounts.waiting
									)}
								</CardText>
							</CardContent>
							<CardTitle component="h3" sx={{ color: 'text.primary' }}>
								{t("dashboard.messages.waiting.title")}
							</CardTitle>
						</FixedHeightPaperCard>
					</Grid>

					<Grid item xs={12} sm={4}>
						<FixedHeightPaperCard>
							<CardContent sx={{ flexDirection: 'column', alignItems: 'center', justifyContent: 'center', mb: 1 }}>
								<CardIconCircle bgcolor="#4caf5020">
									<CheckCircleIcon sx={{ fontSize: 28, color: theme => theme.palette.success.main }} />
								</CardIconCircle>
								<CardText component="h1" variant="h4" sx={{ fontWeight: 700, color: 'text.primary', mb: 0.5 }}>
									{ticketsClosed.count}
									{renderChangeIcon(
										ticketsClosed.count,
										previousCounts.closed
									)}
								</CardText>
							</CardContent>
							<CardTitle component="h3" sx={{ color: 'text.primary' }}>
								{t("dashboard.messages.closed.title")}
							</CardTitle>
						</FixedHeightPaperCard>
					</Grid>
				</Grid>
			</DashboardSection>

			<DashboardSection>
				<Grid container spacing={3}>
					<Grid item xs={12} sm={4}>
						<FixedHeightPaperCard>
							<CardContent sx={{ flexDirection: 'column', alignItems: 'center', justifyContent: 'center', mb: 1 }}>
								<CardIconCircle bgcolor="#2196f320">
									<PeopleIcon sx={{ fontSize: 28, color: theme => theme.palette.info.main }} />
								</CardIconCircle>
								<CardText component="h1" variant="h4" sx={{ fontWeight: 700, color: 'text.primary', mb: 0.5 }}>
									{onlineUsers}/{usersCount}
									{renderChangeIcon(usersCount, previousCounts.users)}
								</CardText>
							</CardContent>
							<CardTitle component="h3" sx={{ color: 'text.primary' }}>
								{t("dashboard.users.title")}
							</CardTitle>
						</FixedHeightPaperCard>
					</Grid>

					<Grid item xs={12} sm={4}>
						<FixedHeightPaperCard>
							<CardContent sx={{ flexDirection: 'column', alignItems: 'center', justifyContent: 'center', mb: 1 }}>
								<CardIconCircle bgcolor="#3f51b520">
									<SendIcon sx={{ fontSize: 28, color: theme => theme.palette.primary.main }} />
								</CardIconCircle>
								<CardText component="h1" variant="h4" sx={{ fontWeight: 700, color: 'text.primary', mb: 0.5 }}>
									{sentMessages}
								</CardText>
							</CardContent>
							<CardTitle component="h3" sx={{ color: 'text.primary' }}>
								{isAdmin ? (
									t("dashboard.messages.sent.titleAdmin")
								) : (
									t("dashboard.messages.sent.title")
								)}
							</CardTitle>
						</FixedHeightPaperCard>
					</Grid>

					<Grid item xs={12} sm={4}>
						<FixedHeightPaperCard>
							<CardContent sx={{ flexDirection: 'column', alignItems: 'center', justifyContent: 'center', mb: 1 }}>
								<CardIconCircle bgcolor="#7c4dff20">
									<ChatIcon sx={{ fontSize: 28, color: theme => theme.palette.secondary.main }} />
								</CardIconCircle>
								<CardText component="h1" variant="h4" sx={{ fontWeight: 700, color: 'text.primary', mb: 0.5 }}>
									{receivedMessages}
								</CardText>
							</CardContent>
							<CardTitle component="h3" sx={{ color: 'text.primary' }}>
								{isAdmin ? (
									t("dashboard.messages.received.titleAdmin")
								) : (
									t("dashboard.messages.received.title")
								)}
							</CardTitle>
						</FixedHeightPaperCard>
					</Grid>
				</Grid>
			</DashboardSection>

			<DashboardSection>
				<Grid container spacing={3}>
					<Grid item xs={12}>
						<LeadMetricsCard whatsappId={selectedChannel || undefined} />
					</Grid>
				</Grid>
			</DashboardSection>

			<DashboardSection>
				<Grid container spacing={3}>
					<Grid item xs={12}>
						<TagCloud />
					</Grid>
				</Grid>
			</DashboardSection>

			{isMasterAdmin && (
				<>
					<DashboardSection>
						<Grid container spacing={3}>
							<Grid item xs={12}>
								<ChartMessages />
							</Grid>
						</Grid>
					</DashboardSection>

					<DashboardSection>
						<Grid container spacing={3}>
							<Grid item xs={12}>
								<ChartPerUser ticketsByUser={ticketsInAttendance.tickets} />
							</Grid>
						</Grid>
					</DashboardSection>

					<DashboardSection>
						<Grid container spacing={3}>
							<Grid item xs={12} sm={6}>
								<NewContactsChart />
							</Grid>
							<Grid item xs={12} sm={6}>
								<ContactsWithTicketsChart />
							</Grid>
						</Grid>
					</DashboardSection>
				</>
			)}
		</StyledContainer>
	);
};

export default Dashboard;
